import ProductDrawerAction from "@/components/drawers/ProductDrawer/ProductDrawerAction";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTableState from "@/components/Table/hooks/useTableState";
import Table, { Column } from "@/components/Table/Table";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import {
  Lifecycle,
  ProductBy,
  ProductImpactDetailPerLifeCycle,
  SingleProductImpactDetailPerLifeCycle,
  useProductImpactDetail,
} from "@/pages/dashboard/hooks/useProductImpact";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { useEffect } from "react";
import { FacilityImpactDetailMode } from "./ProductImpactSideBySide";

export default function ProductImpactDetailsPerLifecycle({
  mode,
  by,
  metric,
}: {
  mode: FacilityImpactDetailMode;
  by: ProductBy;
  metric: ImpactMetric;
}) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination, setSort } =
    useTableState({ defaultPagination: { page: 1, pageSize: 10 } });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const columns: Column<SingleProductImpactDetailPerLifeCycle>[] = [
    {
      label: "Product",
      key: "product_name",
      maxWidth: "2fr",
      searchable: true,
      sortable: true,
      renderer: (record) => (
        <CrossReference to={`/products/${record.id}`}>
          <ProductDrawerAction
            productId={record.id}
            metric={metric}
            by={by}
          >
            {record.product_name}
          </ProductDrawerAction>
        </CrossReference>
      ),
    },
    {
      label: "Raw material",
      key: `${by}_material_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "material"),
      sortable: true,
    },
    {
      label: "Packaging",
      key: `${by}_packaging_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "packaging"),
      sortable: true,
    },
    {
      label: "Production",
      key: `${by}_production_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "production"),
      sortable: true,
    },
    {
      label: "Transport",
      key: `${by}_transport_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "transport"),
      sortable: true,
    },
    {
      label: "Usage",
      key: `${by}_usage_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "usage"),
      sortable: true,
    },
    {
      label: "End of life",
      key: `${by}_eol_${metric.key}`,
      renderer: (record) => impactFormatter(record, data, "eol"),
      sortable: true,
    },
    {
      label: metric.label,
      key: `${by}_product_${metric.key}`,
      sortable: true,
      renderer: (record) => impactFormatter(record, data, "product"),
    },
  ];

  const { data, isLoading, isFetching } = useProductImpactDetail({
    by,
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const impactFormatter = (
    record: SingleProductImpactDetailPerLifeCycle,
    data: ProductImpactDetailPerLifeCycle | undefined,
    column: Lifecycle
  ) => {
    const total = data?.total_impact[column][metric.key];
    const value = record.impact[column][metric.key];

    if (mode === "Absolute") {
      return metric.formatter(value);
    } else {
      return total ? formatPercentage(value / total) : NaN;
    }
  };

  useEffect(() => {
    setSort({ key: `${by}_product_${metric.key}`, order: "descend" });
  }, [metric, by]);

  return (
    <Table
      filter={filter}
      hideRaster={true}
      type={"grey"}
      sort={sort}
      search={search}
      columns={columns}
      data={data?.data}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      totalRows={data?.total_rows}
      rowKey={"key"}
    />
  );
}
